<template>
  <div
    class="w-[300px] max-h-[315px] flex flex-col bg-white rounded-md border border-gray-200/75 z-100 pb-[14px] text-[14px] overflow-hidden"
  >
    <div ref="ownersRef" class="overflow-y-auto">
      <div
        v-for="owner in result?.copyrightOwnersNew.items"
        :key="owner.id"
        class="flex items-center h-12 px-[15px] py-[5px] cursor-pointer font-primary"
        :class="getSelectedClass(owner)"
        @click="handleCopyrightOwnerClick(owner)"
      >
        <Avatar :src="owner.avatarUrl" :size="26" class="mr-2" />
        <div class="flex-grow">
          <div v-truncate class="w-full flex items-center justify-between">
            <div data-truncate-target>{{ owner.name }}</div>
            <Pill v-if="isAccount(owner)" class="mr-[9px]">You</Pill>
          </div>
          <div
            v-if="isOrganization(owner)"
            v-truncate
            class="w-full font-secondary text-[13px] leading-[17px] text-gray-500 flex gap-x-1"
          >
            <div>Managed by</div>
            <div data-truncate-target>{{ owner.owner.name }}</div>
          </div>
        </div>
        <Icon
          v-if="isOrganization(owner)"
          name="Organization"
          :size="16"
          class="stroke-gray-800/50"
        />
        <Icon
          v-if="owner.isSelected"
          name="Check"
          :size="16"
          class="stroke-green-900 ml-auto"
        />
      </div>
    </div>

    <MenuListSeparator class="mt-0" />

    <div
      class="flex items-center px-[14px] cursor-pointer"
      @click="handleNewCopyrightOwnerClick"
    >
      <div class="w-[26px] h-[26px] flex items-center justify-center mr-2">
        <Icon :size="20" name="Plus" class="stroke-green-900" />
      </div>
      <div class="font-secondary font-medium text-[14px] text-green-800">
        New copyright owner
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  CopyrightOwnersNewDocument,
  CopyrightOwnerType,
  useCopyrightOwnersNewQuery,
  useSelectCopyrightOwnerMutation,
  type CopyrightOwnerNew,
  type CopyrightOwnersNewQuery,
} from '@/shared/graphql/legacy/generated'
import { apolloClient } from '@/shared/graphql/client'
import { onBeforeUnmount, ref, watch } from 'vue'
import { MenuListSeparator } from '@/shared/components/Menu'
import { Icon, Pill } from '@dacr/ui'
import { Avatar } from '@dacr/ui'
import { useDevice } from '@/shared/composables/useDevice'
import { Dialogs } from '@/shared/dialogs/types'
import { useDialog } from '@/shared/dialogs/useDialog'

const { result, fetchMore, loading } = useCopyrightOwnersNewQuery()
const { mutate: selectCopyrightOwner } = useSelectCopyrightOwnerMutation()

const device = useDevice()
const { open } = useDialog()

const ownersRef = ref<HTMLElement>()

const getSelectedClass = (owner: CopyrightOwnerNew) => {
  return {
    'bg-green-700/15 hover:bg-initial! font-medium text-green-900':
      owner.isSelected,
    'hover:bg-gray-300/20!': !owner.isSelected,
  }
}

const isAccount = (owner: CopyrightOwnerNew) => {
  return owner.type === CopyrightOwnerType.Account
}

const isOrganization = (owner: CopyrightOwnerNew) => {
  return owner.type === CopyrightOwnerType.Organization
}

const handleCopyrightOwnerClick = async (owner: CopyrightOwnerNew) => {
  document.body.click()

  apolloClient.cache.updateQuery<CopyrightOwnersNewQuery>(
    {
      query: CopyrightOwnersNewDocument,
    },
    (response) => {
      if (!response) return

      const items = response.copyrightOwnersNew.items.map((item) => ({
        ...item,
        isSelected: item.id === owner.id,
      }))

      return {
        ...response,
        copyrightOwnersNew: {
          ...response.copyrightOwnersNew,
          items,
        },
      }
    }
  )

  selectCopyrightOwner({
    id: owner.id,
  })
}

const handleNewCopyrightOwnerClick = () => {
  document.body.click()

  open(Dialogs.NewCopyrightOwner)
}

const handleOwnersScroll = (event: Event) => {
  const element = event.target as HTMLElement
  const elementHeight = element.scrollHeight

  const scrollPos = element.clientHeight + element.scrollTop

  if (scrollPos / elementHeight >= 0.5) {
    fetchNextPageOfOwners()
  }
}

const fetchNextPageOfOwners = () => {
  if (result.value?.copyrightOwnersNew.next && !loading.value) {
    fetchMore({
      variables: {
        next: result.value.copyrightOwnersNew.next,
      },
      updateQuery: (previousData, { fetchMoreResult: newData }) => {
        if (!newData) return previousData

        return {
          ...newData,
          copyrightOwners: {
            ...newData.copyrightOwnersNew,
            items: [
              ...previousData.copyrightOwnersNew.items,
              ...newData.copyrightOwnersNew.items,
            ],
          },
        }
      },
    })
  }
}

watch(ownersRef, () => {
  if (!ownersRef.value) return

  ownersRef.value?.addEventListener('scroll', handleOwnersScroll)
})

onBeforeUnmount(() => {
  ownersRef.value?.removeEventListener('scroll', handleOwnersScroll)
})
</script>
